import { useQuery } from "@tanstack/react-query";

import type { ProjectStylingRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { projectMutations } from "@/queries/project/mutations";
import { useProjectQueries } from "@/queries/project/queryOptions";

import { ProjectStylingContent, type ProjectStylingContentProps } from "./ProjectStylingContent";

export function ProjectStylingPage(): React.ReactNode {
  const { uploadFormImage, isUploadingImage } = useUploadImage();

  const projectQueries = useProjectQueries();
  const {
    data: projectDetails,
    isPending: isLoadingProjectDetails,
    error: projectDetailsError,
  } = useQuery(projectQueries.details());
  const editStylingMutation = projectMutations.useEditProjectStyling();

  const error = projectDetailsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingProjectDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: ProjectStylingContentProps = {
    defaultValues: {
      logoDark: projectDetails?.logo ? [projectDetails.logo] : [],
      logoLight: projectDetails?.logoLight ? [projectDetails.logoLight] : [],
      background: projectDetails?.backgroundImage ? [projectDetails.backgroundImage] : [],
      splashScreenBackground: projectDetails?.splashScreenBackground ? [projectDetails.splashScreenBackground] : [],
      useBackgroundImageForSplashScreen: projectDetails?.useBackgroundImageForSplashScreen || false,
      selectedSplashScreenLogo: projectDetails?.selectedSplashScreenLogo,
      color1: projectDetails?.styling.tintColor,
      color2: projectDetails?.styling.textLightColor,
      color3: projectDetails?.styling.navigationColor,
      pageTitleUnscrolledColor: projectDetails?.styling.pageTitleUnscrolledColor,
      backgroundColor: projectDetails?.styling.backgroundColor,
    },
    isSubmitting: editStylingMutation.isPending || isUploadingImage,
    async onSubmit(data) {
      const payload: ProjectStylingRequest = {
        useBackgroundImageForSplashScreen: data.useBackgroundImageForSplashScreen,
        selectedSplashScreenLogo: data.selectedSplashScreenLogo,
        color1: data.color1,
        color2: data.color2,
        color3: data.color3,
        pageTitleUnscrolledColor: data.pageTitleUnscrolledColor,
        backgroundColor: data.backgroundColor,
      };

      if (data.logoDark.length > 0) {
        const logoDark = await uploadFormImage(data.logoDark[0]);

        payload.imageLogoDarkId = logoDark?.id;
      }
      if (data.logoLight.length > 0) {
        const logoLight = await uploadFormImage(data.logoLight[0]);

        payload.imageLogoLightId = logoLight?.id;
      }
      if (data.background.length > 0) {
        const background = await uploadFormImage(data.background[0]);

        payload.backgroundImageId = background?.id;
      }
      if (data.splashScreenBackground.length > 0) {
        const splashScreenBackground = await uploadFormImage(data.splashScreenBackground[0]);

        payload.splashScreenBackgroundId = splashScreenBackground?.id;
      }

      editStylingMutation.mutate(payload);
    },
  };

  return <ProjectStylingContent {...contentProps} />;
}
